<template>
  <div class="bank-agg-pay-root">
    <div v-if="payLoading" class="pay-loading-mask" aria-live="polite">
      <div class="pay-loading-card">
        <p class="pay-loading-title">{{ loadingTitle }}</p>
        <p class="pay-loading-sub">{{ loadingSub }}</p>
      </div>
    </div>

    <div v-if="pageBooting" class="page">
      <div class="brand">收银台</div>
      <div class="brand-sub">加载中…</div>
    </div>

    <div v-show="!pageBooting" class="page">
      <div class="brand">收银台</div>
      <div class="brand-sub">在线付款</div>

      <div class="card">
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountYuan }}</span>
        </div>

        <div v-if="orderCountdownVisible" class="countdown-row">
          订单剩余 <span class="countdown-val">{{ orderCountdownText }}</span>
        </div>

        <p v-if="hintMain" class="hint">{{ hintMain }}</p>
        <p v-if="hintErr" class="hint err">{{ hintErr }}</p>

        <div class="actions">
          <button
            v-if="needReload"
            type="button"
            class="btn-pay"
            :disabled="pageBooting"
            @click="retryBoot"
          >
            重新加载
          </button>
          <button
            v-else
            type="button"
            class="btn-pay"
            :disabled="btnDisabled"
            @click="handlePayClick"
          >
            立即支付
          </button>
        </div>

        <div class="meta">
          <div class="meta-row"><span>商户订单号</span><span class="meta-val">{{ mchOrderNo || '—' }}</span></div>
          <div class="meta-row"><span>下单时间</span><span class="meta-val">{{ createTime || '—' }}</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatBankAggPageLoadError,
  formatCountdownMmSs,
  loadPageContext,
  startBankAggBuyerOAuth
} from '@/utils/bankAggCashierCore'

const STYLE_ID = 'bank-agg-pay-stylesheet'

export default {
  name: 'BankAggPay',
  data () {
    return {
      pageBooting: true,
      payLoading: false,
      loadingTitle: '正在授权并打开支付',
      loadingSub: '请勿关闭页面',
      channelKey: '',
      orderId: '',
      token: '',
      amountYuan: '--',
      mchOrderNo: '',
      createTime: '',
      expireAtMillis: null,
      invalidToken: false,
      wrongChannel: false,
      expired: false,
      buyerOAuthStateConsumed: false,
      orderPaying: false,
      pageCtx: null,
      pageLoadFailed: false,
      hintMain: '请完成付款',
      hintErr: '',
      orderTimer: null,
      orderCountdownText: '--:--',
      paying: false,
      payGuardTimer: null
    }
  },
  computed: {
    orderCountdownVisible () {
      return !this.invalidToken && !this.wrongChannel && !this.expired && this.expireAtMillis
    },
    needReload () {
      return !!this.pageLoadFailed || (!this.pageBooting && !this.pageCtx)
    },
    btnDisabled () {
      return this.payLoading || this.paying || this.invalidToken || this.wrongChannel || this.expired
        || !this.pageCtx || this.pageLoadFailed
    }
  },
  mounted () {
    this.ensureStyles()
    this.channelKey = String((this.$route.meta && this.$route.meta.channel) || '').toLowerCase()
    this.orderId = String(this.$route.params.orderId || '')
    this.token = String(this.$route.query.t || '')
    this.boot()
  },
  beforeDestroy () {
    this.clearTimers()
  },
  methods: {
    ensureStyles () {
      if (document.getElementById(STYLE_ID)) return
      const link = document.createElement('link')
      link.id = STYLE_ID
      link.rel = 'stylesheet'
      const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
      link.href = `${base}css/bank-agg-pay.css`
      document.head.appendChild(link)
    },
    clearTimers () {
      if (this.orderTimer) clearInterval(this.orderTimer)
      if (this.payGuardTimer) clearTimeout(this.payGuardTimer)
      this.orderTimer = null
      this.payGuardTimer = null
    },
    startOrderCountdown () {
      if (!this.expireAtMillis || this.expired) return
      const tick = () => {
        const left = this.expireAtMillis - Date.now()
        if (left <= 0) {
          this.orderCountdownText = '00:00'
          this.expired = true
          this.hintErr = '订单已过期，请重新下单'
          return
        }
        this.orderCountdownText = formatCountdownMmSs(left)
      }
      tick()
      this.orderTimer = setInterval(tick, 1000)
    },
    retryBoot () {
      if (this.pageBooting) return
      this.boot()
    },
    handlePayClick () {
      if (this.btnDisabled || this.paying) return
      if (this.buyerOAuthStateConsumed || this.orderPaying) {
        this.hintErr = '本单已发起支付，请完成付款或重新下单'
        return
      }
      if (!this.pageCtx) {
        this.pageLoadFailed = true
        this.hintMain = ''
        this.hintErr = this.hintErr || '订单信息未加载成功，请点「重新加载」'
        return
      }
      try {
        const onceKey = 'bankAggSpaPayOnce:' + String(this.orderId || '') + ':' + String(this.token || '').slice(0, 16)
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(onceKey) === '1') {
          return
        }
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(onceKey, '1')
        }
      } catch (e) {
        // ignore
      }
      this.paying = true
      this.payLoading = true
      this.hintErr = ''
      this.loadingTitle = '正在授权并打开支付'
      this.loadingSub = '请勿关闭页面'
      if (this.payGuardTimer) clearTimeout(this.payGuardTimer)
      this.payGuardTimer = setTimeout(() => {
        this.paying = false
        this.payLoading = false
      }, 20000)

      startBankAggBuyerOAuth(this.pageCtx, {
        onProgress: (step) => {
          if (step && step.title) this.loadingTitle = step.title
          if (step && step.sub) this.loadingSub = step.sub
        }
      }).catch((e) => {
        this.paying = false
        this.payLoading = false
        try {
          const onceKey = 'bankAggSpaPayOnce:' + String(this.orderId || '') + ':' + String(this.token || '').slice(0, 16)
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.removeItem(onceKey)
          }
        } catch (ignore) {
          // ignore
        }
        this.hintErr = (e && e.message) || '授权失败，请重试'
      })
    },
    async boot () {
      this.pageBooting = true
      this.pageLoadFailed = false
      this.pageCtx = null
      this.clearTimers()
      this.amountYuan = '--'
      this.mchOrderNo = ''
      this.createTime = ''
      this.expireAtMillis = null
      this.invalidToken = false
      this.wrongChannel = false
      this.expired = false
      this.hintMain = '请完成付款'
      this.hintErr = ''
      try {
        if (!this.orderId) {
          throw new Error('订单号缺失，请重新下单')
        }
        if (!this.token) {
          throw new Error('链接参数缺失，请重新打开支付链接')
        }
        const ctx = await loadPageContext(this.channelKey, this.orderId, this.token)
        this.pageCtx = ctx
        this.amountYuan = ctx.amountYuan || '--'
        this.mchOrderNo = ctx.mchOrderNo || ''
        this.createTime = ctx.createTime || ''
        this.expireAtMillis = ctx.expireAtMillis != null && ctx.expireAtMillis !== ''
          ? Number(ctx.expireAtMillis)
          : null
        this.invalidToken = !!ctx.invalidToken
        this.wrongChannel = !!ctx.wrongChannel
        this.expired = !!ctx.expired
        this.buyerOAuthStateConsumed = !!ctx.buyerOAuthStateConsumed
        this.orderPaying = !!ctx.orderPaying
        this.token = ctx.token || this.token
        if (this.invalidToken) {
          this.hintMain = ''
          this.hintErr = '链接已失效，请重新下单'
          return
        }
        if (this.wrongChannel) {
          this.hintMain = ''
          this.hintErr = '订单类型不匹配，请使用正确支付链接'
          return
        }
        if (this.expired) {
          this.hintMain = ''
          this.hintErr = '订单已过期，请重新下单'
          return
        }
        this.startOrderCountdown()
      } catch (e) {
        this.pageLoadFailed = true
        this.pageCtx = null
        this.amountYuan = '--'
        this.hintMain = ''
        this.hintErr = formatBankAggPageLoadError(e)
      } finally {
        this.pageBooting = false
      }
    }
  }
}
</script>
